import { useDispatch } from "react-redux";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiThatThrows } from "containers/Request";
import { ApiQueryParams, ApiResponse } from "types/api";

import { ReleaseStage } from "./constants";
import { transformDraftToVariantVersion } from "./transformers";
import { TiVoAppVariant, TiVoAppVariantVersion } from "./types";

// Sorts variants by releaseStages priority: submitted > rs1 > rs2 > rs3
const variantVersionSort = (variantVersions: TiVoAppVariantVersion[] = []) => {
  const priorities: ReleaseStage[] = ["rs1", "rs2", "rs3"];
  const getPriority = (releaseStages: ReleaseStage[] = []) => {
    for (let i = 0; i < priorities.length; i++) {
      if (releaseStages.includes(priorities[i])) {
        return i;
      }
    }
    return -1;
  };
  return [...variantVersions].sort((a, b) => {
    return getPriority(a.releaseStages) - getPriority(b.releaseStages);
  });
};

export const useVariant = (
  id?: string,
  variantId?: string,
  queryParams: ApiQueryParams = {}
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useQuery<
    ApiResponse<TiVoAppVariant[]>,
    Error,
    ApiResponse<TiVoAppVariant | undefined>
  >({
    queryKey: ["variant", id, variantId, queryParams],
    queryFn: async () => {
      const data: ApiResponse<TiVoAppVariant[]> = await dispatch(
        apiThatThrows.getTiVoVariants.action({
          params: { id },
          queryParams,
        })
      );

      data.results = data.results.map((variant) => {
        if (variant.draftVersion) {
          queryClient.setQueryData(["variantVersionDraft", id, variant.id], {
            results: variant.draftVersion,
          });
        }
        return {
          ...variant,
          versions: [
            ...(variant.draftVersion
              ? [transformDraftToVariantVersion(variant.draftVersion)]
              : []),
            ...variantVersionSort(variant.versions || []),
          ],
        };
      });

      return data;
    },

    enabled: Boolean(id) && Boolean(variantId), // don't run if no id
    select: ({ results, ...rest }) => {
      const variant = results.find(({ id }) => id === variantId);
      return {
        ...rest,
        results: variant,
      };
    },
  });
};
